import { Button } from "@amzn/awsui-components-react/polaris";
import { getExcaliburAuditData, getTransactionDetailsData } from '../../excalibur/components/excaliburAPIs';
import orderBy from 'lodash/orderBy';
import {populateReportingPeriod} from "../utils/Utils"
import {REMOTE_FILING_TRANSACTION_TYPE, TRANSACTION_DETAIL_INFO} from "../../../common/constants/constants";
import {getLoadedPageCount, mergeRows, needsMoreRows} from "../../../common/pagination/paging";

// Backend calls made in a row to fill the pager window after one page change; stops a backend that
// keeps answering with few rows and a cursor from looping.
const MAX_CHAINED_FETCHES = 3;

export const INITIAL_STATE = {
    data: [],
    reportingPeriod: null,
    periodList: [],
    lastEvaluatedKey: null,
    auditRows: [],
    pageCount: 1,
    currentPageIndex: 1,
    fetching: false,
    chainedFetches: 0,
    requestToken: 0,
    sortingColumn: { "sortingField": "startTimestamp" },
    sortingDescending: true,
    loading: true,
    loadingText: "Loading requests",
    initialize: true,
    filePath: {},
    flashBarItem: [],
    submitted: false,
    fileSummaryData : null
};

export const filingReducerInit = initialState => {
    return initialState;
}

// Which list this reducer instance pages: the filing audit list, or one transaction's task rows.
const isTaskTable = ({tableContext}) => tableContext === TRANSACTION_DETAIL_INFO;
const rowKeyFor = target => isTaskTable(target) ? "taskId" : "transactionId";

// Tags every response with the token of the request that asked for it, so a response from before a
// reload is dropped instead of merged into the new rows.
const fetchAuditPage = (dispatcher, startKey, requestToken, target) => {
    const {tableContext, transactionId} = target;
    const tagged = action => dispatcher({
        ...action,
        payload: {...action.payload, requestToken, dispatcher, tableContext, transactionId}
    });
    if (isTaskTable(target)) {
        getTransactionDetailsData(tagged, startKey, transactionId);
    } else {
        getExcaliburAuditData(tagged, startKey, REMOTE_FILING_TRANSACTION_TYPE);
    }
}

const sortAuditRows = (rows, state) =>
    orderBy(rows, state.sortingColumn.sortingField, state.sortingDescending ? 'desc' : 'asc');

// Drops the loaded list and fetches page one again; the upload form's state is left alone.
const reloadAudit = (state, payload) => {
    const requestToken = state.requestToken + 1;
    fetchAuditPage(payload.dispatcher, null, requestToken, payload);
    return {
        ...state,
        lastEvaluatedKey: null,
        auditRows: [],
        pageCount: 1,
        currentPageIndex: 1,
        fetching: true,
        chainedFetches: 0,
        requestToken,
        loading: true,
        initialize: false
    };
}


export const filingReducer = (state, action) => {
    switch (action.type) {
        case "INITIALIZE_AUDIT": {
            if (!state.initialize) {
                return state;
            }
            fetchAuditPage(action.payload.dispatcher, null, state.requestToken, action.payload);
            return {
                ...state,
                initialize: false,
                fetching: true,
                loading: true
            }
        }
        case "API_RESPONSE": {
            if (action.payload.requestToken !== state.requestToken) {
                return state;
            }
            const auditRows = sortAuditRows(
                mergeRows(state.auditRows, action.payload.data, rowKeyFor(action.payload)), state);
            const pageCount = getLoadedPageCount(auditRows);
            const lastEvaluatedKey = action.payload.lastEvaluatedKey || null;
            let currentPageIndex = state.currentPageIndex;
            const chain = needsMoreRows(currentPageIndex, pageCount, lastEvaluatedKey)
                && state.chainedFetches < MAX_CHAINED_FETCHES;
            if (chain) {
                fetchAuditPage(action.payload.dispatcher, lastEvaluatedKey, state.requestToken, action.payload);
            } else {
                // The page stepped onto turned out not to exist: back onto the last loaded one.
                currentPageIndex = Math.min(currentPageIndex, pageCount);
            }
            return {
                ...state,
                auditRows,
                pageCount,
                lastEvaluatedKey,
                currentPageIndex,
                fetching: chain,
                chainedFetches: chain ? state.chainedFetches + 1 : 0,
                loading: chain && currentPageIndex > pageCount,
                initialize: false
            }
        }
        case "CREATE_FILE_PATH_SUCCESS":
            return {
                ...state,
                filePath: action.payload?.data
            }
        case "TRANSMIT_FILE_REQUEST_COMPLETED": {
            const next = action.payload.fetchAudit ? reloadAudit(state, action.payload) : state;
            return {
                ...next,
                submitted: true
            }
        }

        case "SET_FLASH_BAR":
            let buttonCTA = action.payload.type === 'success' ? 'Continue Filing' : 'Retry';
            window.scrollTo(0,0);
            return {
                ...state,
                flashBarItem: [{
                    type: action.payload?.type,
                    action: action.payload?.handleFlashBarAction ? <Button onClick={action.payload?.handleFlashBarAction}>{buttonCTA}</Button> : null,
                    dismissible: true,
                    dismissLabel: "Dismiss message",
                    onDismiss: action.payload?.handleFlashBarAction? () => {
                        action.payload?.dispatcher({ type: "DISMISS_FLASHBAR", payload: {} })
                    } : null ,
                    content: (
                        <>
                            {action.payload?.msg}
                        </>
                    ),
                    id: "message_1"
                }]
            }
        case "FAILURE_API_RESPONSE": {
            // Only a failed audit fetch carries a token; failures of other filing calls leave the list alone.
            if (action.payload?.requestToken !== state.requestToken) {
                return state;
            }
            return {
                ...state,
                fetching: false,
                chainedFetches: 0,
                loading: false,
                initialize: false,
                currentPageIndex: Math.min(state.currentPageIndex, state.pageCount)
            }
        }
        case "AUTH_UPDATE":
            console.log(action.payload)
            return {
                ...state,
                isUserAuthenticatedWithTaxAuthority : action.payload.isUserAuthenticatedWithTaxAuthority
            }
        case "SET_AUTH_CODE":
            return {
                ...state,
                authCode: action.payload?.data
            }
        case "SET_REPORTING_PERIOD":
            const data = populateReportingPeriod(action.payload?.periodList);
            return {
                ...state,
                reportingPeriod: data,
                periodList: action.payload?.periodList
            }
        case "DISMISS_FLASHBAR":
            return {
                ...state,
                flashBarItem: []
            }
        
        case "PAGE_INDEX_CHANGED": {
            const hasMore = Boolean(state.lastEvaluatedKey);
            let pageIndex = action.payload.pageIndex;
            if (pageIndex > state.pageCount && !hasMore) {
                pageIndex = state.pageCount;
            }
            const startFetch = !state.fetching && needsMoreRows(pageIndex, state.pageCount, hasMore);
            if (startFetch) {
                fetchAuditPage(action.payload.dispatcher, state.lastEvaluatedKey, state.requestToken, action.payload);
            }
            const fetching = state.fetching || startFetch;
            return {
                ...state,
                currentPageIndex: pageIndex,
                fetching,
                chainedFetches: 0,
                loading: fetching && pageIndex > state.pageCount
            }
        }
        case "RESET_STATE": 
            return {
                ...state,
                periodList : action.payload.periodList,
                filePath : action.payload.filePath,
                fileSummaryData : action.payload.fileSummaryData,
                flashBarItem : action.payload.flashBarItem,
                submitted : action.payload.submitted,
                reportingPeriod : action.payload.reportingPeriod
            } 
        case "SET_SUMMARY_DATA": 
            return {
                ...state,
                fileSummaryData : action.payload.fileSummaryData
            }  
        case "AUDIT_SORTING_CHANGED": {
            const sorted = {
                ...state,
                sortingDescending: action.payload.isDescending,
                sortingColumn: {"sortingField": action.payload.sortingField}
            };
            return {
                ...sorted,
                auditRows: sortAuditRows(state.auditRows, sorted)
            }
        }
        case "REFRESH_AUDIT":
            return reloadAudit(state, action.payload);

        default:
            return state;
    }
}